import { useState, useEffect, useMemo, useCallback } from 'react';
import { NON_EDITABLE_STATUSES, editBlockedMessage } from '../../lib/orderEditPolicy';

export const TIME_WINDOWS = [
  '7:00 AM - 9:00 AM',
  '9:00 AM - 11:00 AM',
  '11:00 AM - 1:00 PM',
  '1:00 PM - 3:00 PM',
  '3:00 PM - 5:00 PM',
];

const newUid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const sameName = (a, b) =>
  (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const line = (fields) => {
  const quantity = Math.max(1, parseInt(fields.quantity, 10) || 1);
  const unit_price = Math.max(0, parseFloat(fields.unit_price) || 0);
  return {
    _uid: fields._uid || newUid(),
    product_id: fields.product_id || '',
    product_name: fields.product_name || '',
    pack_size: fields.pack_size || '',
    quantity,
    unit_price,
    subtotal: +(quantity * unit_price).toFixed(2),
    is_custom_item: !fields.product_id,
  };
};

const blankForm = (defaultDate) => ({
  customer_name: '',
  order_type: 'delivery',
  order_date: defaultDate || '',
  baking_date: '',
  delivery_address: '',
  delivery_city: '',
  delivery_state: '',
  delivery_zip: '',
  delivery_instructions: '',
  delivery_time_window: TIME_WINDOWS[0],
  pickup_time: '',
  notes: '',
});

const formFromOrder = (order) => {
  const deliveryDate = (order.delivery_date || order.production_date || '').slice(0, 10);
  const bakingDate = (order.production_date || '').slice(0, 10);
  return {
    customer_name: order.customer_name || '',
    order_type: order.order_type || 'delivery',
    order_date: deliveryDate,
    // The baking day follows the delivery date unless it was deliberately set
    // to a different day.
    baking_date: bakingDate && bakingDate !== deliveryDate ? bakingDate : '',
    delivery_address: order.delivery_address || '',
    delivery_city: order.delivery_city || '',
    delivery_state: order.delivery_state || '',
    delivery_zip: order.delivery_zip || '',
    delivery_instructions: order.delivery_instructions || '',
    delivery_time_window: order.delivery_time_window || TIME_WINDOWS[0],
    pickup_time: order.pickup_time || '',
    notes: order.notes || '',
  };
};

// One place for the state and the rules behind the custom-order form, shared by
// the admin and the staff portal so there is only ever one way to book a
// walk-in.
export const useCustomOrderForm = ({ open, order, defaultDate }) => {
  const [form, setForm] = useState(blankForm(defaultDate));
  const [items, setItems] = useState([]);
  const [showBakingDay, setShowBakingDay] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (order) {
      const next = formFromOrder(order);
      setForm(next);
      setShowBakingDay(!!next.baking_date);
      setItems((order.items || []).map((i) => line(i)));
    } else {
      setForm(blankForm(defaultDate));
      setShowBakingDay(false);
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id, defaultDate]);

  const setField = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  }, []);

  const patchLine = useCallback((uid, fields) => {
    setItems((prev) => prev.map((i) => (i._uid === uid ? line({ ...i, ...fields }) : i)));
  }, []);

  const addProduct = useCallback((product) => {
    setItems((prev) => {
      if (prev.some((i) => i.product_id && i.product_id === product.id)) return prev;
      return [...prev, line({
        product_id: product.id,
        product_name: product.name,
        pack_size: product.pack_size || '',
        quantity: 1,
        unit_price: product.price,
      })];
    });
  }, []);

  const addOneOff = useCallback(() => {
    setItems((prev) => [...prev, line({ quantity: 1, unit_price: 0 })]);
  }, []);

  // Swap an invented line for the catalog product it turned out to name, so a
  // near-miss spelling cannot become a second line in the kitchen's totals.
  const linkToProduct = useCallback((uid, product) => {
    setItems((prev) => prev.map((i) => (
      i._uid === uid
        ? line({
          ...i,
          product_id: product.id,
          product_name: product.name,
          pack_size: product.pack_size || '',
          unit_price: i.unit_price > 0 ? i.unit_price : product.price,
        })
        : i
    )));
  }, []);

  const removeItem = useCallback((uid) => {
    setItems((prev) => prev.filter((i) => i._uid !== uid));
  }, []);

  const total = useMemo(
    () => +items.reduce((sum, i) => sum + (i.subtotal || 0), 0).toFixed(2),
    [items],
  );

  const blockedReason = order && NON_EDITABLE_STATUSES.has(order.status)
    ? editBlockedMessage(order.status)
    : null;

  const validate = () => {
    if (!form.customer_name.trim()) return 'A customer name is needed.';
    if (!form.order_date) return `A ${form.order_type === 'pickup' ? 'pickup' : 'delivery'} date is needed.`;
    if (items.filter((i) => i.product_name.trim() && i.quantity > 0).length === 0) {
      return 'Add at least one item — pick a product or add a one-off.';
    }
    if (items.some((i) => !i.product_name.trim())) return 'Every one-off line needs a name.';
    return null;
  };

  const buildPayload = () => ({
    customer_name: form.customer_name.trim(),
    order_type: form.order_type,
    delivery_date: form.order_date,
    production_date: (showBakingDay && form.baking_date) ? form.baking_date : form.order_date,
    delivery_address: form.order_type === 'delivery' ? form.delivery_address : '',
    delivery_city: form.order_type === 'delivery' ? form.delivery_city : '',
    delivery_state: form.order_type === 'delivery' ? form.delivery_state : '',
    delivery_zip: form.order_type === 'delivery' ? form.delivery_zip : '',
    delivery_instructions: form.order_type === 'delivery' ? form.delivery_instructions : '',
    delivery_time_window: form.order_type === 'delivery' ? form.delivery_time_window : '',
    pickup_time: form.order_type === 'pickup' ? form.pickup_time : '',
    notes: form.notes,
    items: items
      .filter((i) => i.product_name.trim() && i.quantity > 0)
      .map(({ _uid, ...rest }) => rest),
  });

  return {
    form,
    setField,
    items,
    showBakingDay,
    setShowBakingDay,
    addProduct,
    addOneOff,
    patchLine,
    linkToProduct,
    removeItem,
    total,
    blockedReason,
    validate,
    buildPayload,
  };
};
